import { IbePriceModelCode } from '@gecko-akh/pkg-cruise-api-types/src/iris'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import { Direction } from '../../../bewotec/openapi'
import { convertFormat, DEFAULT_DATE_FORMAT, getValidDateFormat, ISO_DATE_FORMAT } from '../../../general/helper.js'
import { CLASSICAI, COMFORTAI, INDAI } from '../../../general/master-data.js'
import { IPaxConfig } from '../../../store/catalog/interfaces.js'
import { IExpinetFlexFlightType } from '../flex-flight/interfaces.js'
import { IExpinetSelectedFlight } from '../flight/interfaces'
import { mapPriceModelLabel } from '../helper.js'
import { IFetchRailOption } from '../rail/interfaces.js'
import { IPerson, IService, ShipServiceParams } from './interfaces.js'

dayjs.locale('de')

export const makePaxIds = (paxConfig: IPaxConfig) => {
  const paxIds: number[] = new Array(paxConfig.total)

  for (let i = 0; i < paxConfig.total; ++i) {
    paxIds[i] = i + 1
  }

  return paxIds
}

export const makeShipService = (shipServiceParams: ShipServiceParams): IService => {
  const startDate = convertFormat(shipServiceParams.cruise.startDate, DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT)
  const endDate = convertFormat(shipServiceParams.cruise.endDate, DEFAULT_DATE_FORMAT, ISO_DATE_FORMAT)

  const shipService: IService = {
    type: 'P',
    code: shipServiceParams.ship.id,
    accomodation:
      shipServiceParams.isCabinSelectable && !!shipServiceParams.cabin?.id ? shipServiceParams.cabin.id : '',
    category: shipServiceParams.category?.id ?? '',
    startDate,
    endDate,
    numPaxe: shipServiceParams.paxConfig.total,
    paxIds: shipServiceParams.paxIds,
  }

  return shipService
}

export const createRailService = (
  paxIds: number[],
  paxConfig: IPaxConfig,
  direction: string,
  railOption: IFetchRailOption
): IService => {
  return {
    type: 'BA',
    code: railOption.railTarifCode,
    accomodation: getRailDirection(direction),
    category: railOption.railClassCode,
    startDate: '',
    endDate: '',
    numPaxe: paxConfig.total,
    paxIds,
  }
}

const getRailDirection = (direction: string) => {
  switch (direction) {
    case 'both':
      return 'B'
    case 'inbound':
      return 'R'
    case 'outbound':
      return 'H'
    default:
      return 'B'
  }
}

export const createFlightServices = (
  paxIds: number[],
  paxConfig: IPaxConfig,
  selectedFlight: IExpinetSelectedFlight | null
): IService[] => {
  const services = []

  const outboundFlight = selectedFlight?.outboundFlight
  const inboundFlight = selectedFlight?.inboundFlight

  if (!!outboundFlight) {
    const outboundDepartureTime = outboundFlight.departure.time

    const outboundFlightService = {
      type: 'F',
      code: outboundFlight.departure.airportId,
      accomodation: outboundFlight?.direction === Direction.RT ? '' : 'H',
      category: outboundFlight.mainFlight.flightClass.id,
      startDate: !!outboundDepartureTime
        ? convertFormat(outboundDepartureTime, getValidDateFormat(outboundDepartureTime), 'YYYY-MM-DD')
        : '',
      endDate: '',
      numPaxe: paxConfig.total,
      paxIds,
    }

    services.push(outboundFlightService)
  }

  if (!!inboundFlight && inboundFlight?.direction !== Direction.RT) {
    const inboundArrivalTime = inboundFlight.arrival.time

    const inboundFlightService = {
      type: 'F',
      code: inboundFlight.arrival.airportId,
      accomodation: 'R',
      category: inboundFlight.mainFlight.flightClass.id,
      startDate: !!inboundArrivalTime
        ? convertFormat(inboundArrivalTime, getValidDateFormat(inboundArrivalTime), 'YYYY-MM-DD')
        : '',
      endDate: '',
      numPaxe: paxConfig.total,
      paxIds,
    }

    services.push(inboundFlightService)
  }

  return services
}

export const createFlexFlightServices = (
  paxIds: number[],
  paxConfig: IPaxConfig,
  selectedFlight: IExpinetFlexFlightType | null
): IService[] => {
  if (!selectedFlight) return []

  const services = []

  const outboundFlight = selectedFlight.outboundFlight
  const inboundFlight = selectedFlight.inboundFlight

  if (!!outboundFlight) {
    const outboundFlightService = {
      type: 'F',
      code: selectedFlight.id,
      accomodation: selectedFlight.direction === Direction.RT ? '' : 'H',
      category: outboundFlight.mainFlight.flightClass.id,
      startDate: convertFormat(
        outboundFlight.departure.time,
        getValidDateFormat(outboundFlight.departure.time),
        'YYYY-MM-DD'
      ),
      endDate: '',
      numPaxe: paxConfig.total,
      paxIds,
      mark: 'x',
    }

    services.push(outboundFlightService)
  }

  if (
    !!inboundFlight &&
    selectedFlight.direction !== Direction.RT &&
    'flightClass' in inboundFlight
  ) {
    const inboundFlightService = {
      type: 'F',
      code: selectedFlight.id,
      accomodation: 'R',
      category: inboundFlight.mainFlight.flightClass.id,
      startDate: convertFormat(
        inboundFlight.arrival.time,
        getValidDateFormat(inboundFlight.arrival.time),
        'YYYY-MM-DD'
      ),
      endDate: '',
      numPaxe: paxConfig.total,
      paxIds,
      mark: 'x',
    }

    services.push(inboundFlightService)
  }

  return services
}

export const makeDummyPassengers = (
  paxConfig: IPaxConfig,
  serviceLength: number,
  birthdates?: object,
  railAdults?: number
): IPerson[] => {
  const passengers: IPerson[] = []
  let totalPaxes = 0

  for (let i = 0; i < paxConfig.adults; ++i) {
    passengers.push(getPassenger(i, 40))
  }

  totalPaxes += paxConfig.adults

  for (let i = 0; i < paxConfig.juveniles; ++i) {
    passengers.push(getPassenger(totalPaxes + i, 18))
  }

  totalPaxes += paxConfig.juveniles

  for (let i = 0; i < paxConfig.children + paxConfig.babies; ++i) {
    const hasTransportation = serviceLength > 1
    const hasRailPayingChildren = !!railAdults && i < railAdults
    const hasBirthdates = !!birthdates && i < Object.values(birthdates).length

    if (hasTransportation && hasRailPayingChildren) {
      passengers.push(getPassenger(totalPaxes + i, 15))
    } else if (hasTransportation && hasBirthdates) {
      passengers.push(
        getPassenger(
          totalPaxes + i,
          15,
          dayjs(Object.values(birthdates)[i], DEFAULT_DATE_FORMAT).format(ISO_DATE_FORMAT)
        )
      )
    } else {
      const isChild = i < paxConfig.children

      passengers.push(getPassenger(totalPaxes + i, isChild ? 8 : 1))
    }
  }

  return passengers
}

export const getBeverageText = (priceModel: IbePriceModelCode) => {
  switch (priceModel) {
    case COMFORTAI:
    case INDAI:
    case CLASSICAI:
      return `${mapPriceModelLabel(priceModel)} inkl. Getränkepaket`
    default:
      return mapPriceModelLabel(priceModel)
  }
}

const getPassenger = (index: number, age: number, birthdate?: string) => {
  const persons = [
    { firstName: 'Max Test', salutation: 'H' },
    { firstName: 'Marie Test', salutation: 'D' },
    { firstName: 'Tom Test', salutation: 'H' },
    { firstName: 'Susi Test', salutation: 'D' },
    { firstName: 'Nele Test', salutation: 'D' },
  ]

  return {
    salutation: persons[index].salutation,
    firstName: persons[index].firstName,
    lastName: 'Mustermann',
    dateOfBirth: birthdate ?? dayjs().subtract(age, 'year').format(ISO_DATE_FORMAT),
    nationality: 'DE',
  }
}

export const priceHelper = {
  makeShipService,
  createFlightServices,
  createFlexFlightServices,
  createRailService,
}

// --------------- Export for testing purposes only ---------------

export const exportForTesting = {
  getRailDirection,
  getPassenger,
}
